import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { GraduationCap } from 'lucide-react';

import { Button } from '../ui/button';
import { settingsApi } from '../../services/api';
import { useSettingsQuery } from '../../hooks/queries';
import { settingsKeys } from '../../lib/queryKeys';
import { isAllComplete } from '../../data/tutorials';
import SpotlightOverlay, { useTargetRect } from './SpotlightOverlay';

const SpotlightIntro = () => {
  const { t } = useTranslation();
  const settingsQuery = useSettingsQuery();
  const queryClient = useQueryClient();
  const [dismissing, setDismissing] = useState(false);

  const settings = settingsQuery.data;
  const onboardingDone = settings?.onboarding_completed === true;
  const firstViewSeen = settings?.tutorial_first_view_seen === true;
  const allDone = isAllComplete(settings?.tutorial_progress || {});

  // Desktop only — match the trigger's md: visibility so the spotlight
  // never points at empty space on mobile.
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const shouldShow =
    !!settings && onboardingDone && !firstViewSeen && !allDone && isDesktop;

  const rect = useTargetRect('[data-tutorial-trigger="true"]', shouldShow);

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await settingsApi.update({ tutorial_first_view_seen: true });
      await queryClient.invalidateQueries({ queryKey: settingsKeys.current() });
    } catch (e) {
      console.error('Failed to dismiss tutorial intro', e);
    } finally {
      setDismissing(false);
    }
  };

  if (!shouldShow || !rect) return null;

  return (
    <SpotlightOverlay rect={rect} placement="bottom" onDismiss={dismiss} dismissable>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400">
            <GraduationCap className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold">{t('tutorials.intro.title')}</h3>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {t('tutorials.intro.body')}
            </p>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={dismiss} disabled={dismissing} className="h-8">
            {t('tutorials.intro.dismiss')}
          </Button>
        </div>
      </div>
    </SpotlightOverlay>
  );
};

export default SpotlightIntro;
